import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { useDroppable } from '@dnd-kit/core'
import {
  Button,
  Group,
  Stack,
  Text,
  SimpleGrid,
  Paper,
  Box,
  Badge,
  Modal,
  Tabs,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconPlus,
  IconDeviceFloppy,
  IconRefresh,
  IconArrowsShuffle,
  IconFlask,
  IconLayoutGrid,
  IconMap2,
} from '@tabler/icons-react'
import type {
  Guest,
  SeatingConfiguration,
  SeatingRoomConfig,
  SeatingState,
  SeatingTestGuestDraft,
} from '@/lib/types'
import {
  UNASSIGNED_ZONE_ID,
  autoDistributeGroups,
  clampPosition,
  createDefaultTable,
  ensureTablePositions,
  getAllSeatingGroups,
  getGroupsForTable,
  getUnassignedGroups,
  normalizeRoomConfig,
  sanitizeSeatingState,
  testGuestDraftToSeatingTestGuest,
  withPlacedTables,
  type SeatingGroup,
} from '@/lib/seatingUtils'
import { GuestGroupCard } from './GuestGroupCard'
import { SeatingTableCard } from './SeatingTableCard'
import { SeatingConfigurationBar } from './SeatingConfigurationBar'
import { RoomPlanEditor } from './RoomPlanEditor'
import { TestGuestModal } from './TestGuestModal'
import { PromoteTestGuestModal } from './PromoteTestGuestModal'
import styles from './SeatingChart.module.css'

interface SeatingChartProps {
  /** Pre-filtered to rsvp_status === 'coming' */
  guests: Guest[]
  savedState: SeatingState
  saving: boolean
  promoting: boolean
  configBusy: boolean
  configurations: SeatingConfiguration[]
  activeConfigurationId: string | null
  activeConfigurationName: string
  onSave: (state: SeatingState) => Promise<boolean>
  onPromoteTestGuest: (testGuestId: string, state: SeatingState) => Promise<boolean>
  onGuestsChanged: () => Promise<void>
  onSelectConfiguration: (id: string) => Promise<void>
  onCreateConfiguration: (name: string, duplicateFromId?: string) => Promise<boolean>
  onUpdateConfigurationName: (name: string) => Promise<boolean>
  onDeleteConfiguration: (id: string) => Promise<boolean>
}

interface TestGuestCardHandlers {
  onEditTestGuest?: (group: SeatingGroup) => void
  onDeleteTestGuest?: (group: SeatingGroup) => void
  onPromoteTestGuest?: (group: SeatingGroup) => void
}

function UnassignedZone({
  groups,
  activeDragId,
  onEditTestGuest,
  onDeleteTestGuest,
  onPromoteTestGuest,
}: {
  groups: SeatingGroup[]
  activeDragId: string | null
} & TestGuestCardHandlers) {
  const { setNodeRef, isOver } = useDroppable({ id: UNASSIGNED_ZONE_ID })
  const totalPeople = groups.reduce((sum, g) => sum + g.personCount, 0)

  return (
    <Paper
      ref={setNodeRef}
      p="sm"
      shadow="xs"
      withBorder
      className={`${styles.unassignedZone} ${isOver ? styles.dropTargetActive : ''}`}
    >
      <Group justify="space-between" mb={4} gap="xs">
        <Text size="sm" fw={600}>
          Noch nicht zugewiesen
        </Text>
        <Badge size="lg" variant="light" color="gray" className={styles.countBadge}>
          {totalPeople} Pers.
        </Badge>
      </Group>
      <Box className={styles.unassignedList}>
        {groups.length === 0 ? (
          <Box className={styles.emptyDropZone}>
            <Text size="xs" c="dimmed" ta="center">
              Alle Gäste sind zugewiesen
            </Text>
          </Box>
        ) : (
          groups.map((group) => (
            <GuestGroupCard
              key={group.id}
              group={group}
              isDragging={activeDragId === group.id}
              onEditTestGuest={onEditTestGuest}
              onDeleteTestGuest={onDeleteTestGuest}
              onPromoteTestGuest={onPromoteTestGuest}
            />
          ))
        )}
      </Box>
    </Paper>
  )
}

function testGuestToDraft(group: SeatingGroup): SeatingTestGuestDraft | null {
  if (!group.testGuest) return null
  return {
    id: group.testGuest.id,
    primary_name: group.testGuest.primary_name,
    additional_guests: (group.testGuest.additional_guests ?? []).map((ag) => ({
      id: ag.id,
      name: ag.name,
      type: ag.type as SeatingTestGuestDraft['additional_guests'][number]['type'],
      age: ag.age,
    })),
  }
}

export function SeatingChart({
  guests,
  savedState,
  saving,
  promoting,
  configBusy,
  configurations,
  activeConfigurationId,
  activeConfigurationName,
  onSave,
  onPromoteTestGuest,
  onGuestsChanged,
  onSelectConfiguration,
  onCreateConfiguration,
  onUpdateConfigurationName,
  onDeleteConfiguration,
}: SeatingChartProps) {
  const attendingGroups = guests
  const [draft, setDraft] = useState<SeatingState>(() =>
    sanitizeSeatingState(savedState, attendingGroups)
  )
  const [activeDragId, setActiveDragId] = useState<string | null>(null)
  const [testGuestModalOpen, setTestGuestModalOpen] = useState(false)
  const [editingTestGuest, setEditingTestGuest] = useState<SeatingTestGuestDraft | null>(null)
  const [promoteGroup, setPromoteGroup] = useState<SeatingGroup | null>(null)
  const [deleteGroup, setDeleteGroup] = useState<SeatingGroup | null>(null)
  const [activeTab, setActiveTab] = useState<string | null>('assignment')

  useEffect(() => {
    setDraft(sanitizeSeatingState(savedState, attendingGroups))
  }, [savedState, attendingGroups])

  const allGroups = useMemo(
    () => getAllSeatingGroups(attendingGroups, draft.testGuests),
    [attendingGroups, draft.testGuests]
  )

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  )

  const unassignedGroups = useMemo(
    () => getUnassignedGroups(allGroups, draft.assignments),
    [allGroups, draft.assignments]
  )

  const activeGroup = useMemo(
    () => allGroups.find((g) => g.id === activeDragId) ?? null,
    [allGroups, activeDragId]
  )

  const hasChanges = useMemo(() => {
    const sanitizedDraft = sanitizeSeatingState(draft, attendingGroups)
    const sanitizedSaved = sanitizeSeatingState(savedState, attendingGroups)
    return JSON.stringify(sanitizedDraft) !== JSON.stringify(sanitizedSaved)
  }, [draft, savedState, attendingGroups])

  const testGuestHandlers: TestGuestCardHandlers = {
    onEditTestGuest: (group) => {
      const draftData = testGuestToDraft(group)
      if (!draftData) return
      setEditingTestGuest(draftData)
      setTestGuestModalOpen(true)
    },
    onDeleteTestGuest: (group) => setDeleteGroup(group),
    onPromoteTestGuest: (group) => setPromoteGroup(group),
  }

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id))
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragId(null)
    const { active, over } = event
    if (!over) return

    const groupId = String(active.id)
    const targetId = String(over.id)

    setDraft((prev) => {
      const newAssignments = { ...prev.assignments }

      let tableId: string | null = null

      if (targetId === UNASSIGNED_ZONE_ID) {
        tableId = null
      } else if (prev.tables.some((t) => t.id === targetId)) {
        tableId = targetId
      } else {
        tableId = prev.assignments[targetId] ?? null
      }

      newAssignments[groupId] = tableId
      return { ...prev, assignments: newAssignments }
    })
  }, [])

  const handleAddTable = useCallback(() => {
    setDraft((prev) => {
      const newTable = createDefaultTable(prev.tables.length + 1, prev.configurationId)
      const nextTables = [...prev.tables, newTable]
      return {
        ...prev,
        tables: activeTab === 'room' ? ensureTablePositions(nextTables, prev.room) : nextTables,
      }
    })
  }, [activeTab])

  const handleRemoveTable = useCallback((tableId: string) => {
    setDraft((prev) => {
      const newAssignments = { ...prev.assignments }
      for (const [groupId, assignedTableId] of Object.entries(newAssignments)) {
        if (assignedTableId === tableId) {
          newAssignments[groupId] = null
        }
      }
      return {
        ...prev,
        tables: prev.tables.filter((t) => t.id !== tableId),
        assignments: newAssignments,
      }
    })
  }, [])

  const handleTableNameChange = useCallback((tableId: string, name: string) => {
    setDraft((prev) => ({
      ...prev,
      tables: prev.tables.map((t) => (t.id === tableId ? { ...t, name } : t)),
    }))
  }, [])

  const handleAutoDistribute = useCallback(() => {
    if (draft.tables.length === 0) {
      notifications.show({
        title: 'Keine Tische',
        message: 'Bitte fügen Sie zuerst Tische hinzu.',
        color: 'orange',
      })
      return
    }

    setDraft((prev) => ({
      ...prev,
      assignments: autoDistributeGroups(allGroups, prev.tables),
    }))
  }, [allGroups, draft.tables.length])

  const handleReset = useCallback(() => {
    setDraft(sanitizeSeatingState(savedState, attendingGroups))
  }, [savedState, attendingGroups])

  const handleMoveTable = useCallback((tableId: string, x: number, y: number) => {
    setDraft((prev) => ({
      ...prev,
      tables: prev.tables.map((t) =>
        t.id === tableId ? { ...t, position_x: x, position_y: y } : t
      ),
    }))
  }, [])

  const handleRoomChange = useCallback((patch: Partial<SeatingRoomConfig>) => {
    setDraft((prev) => {
      const room = normalizeRoomConfig({ ...prev.room, ...patch })
      const radius = room.tableDiameter / 2
      const tables = prev.tables.map((t) => {
        if (t.position_x == null || t.position_y == null) return t
        return {
          ...t,
          position_x: clampPosition(t.position_x, radius, room.roomWidth),
          position_y: clampPosition(t.position_y, radius, room.roomHeight),
        }
      })
      return { ...prev, room, tables }
    })
  }, [])

  const placedTables = useMemo(
    () => withPlacedTables(draft.tables, draft.room),
    [draft.tables, draft.room]
  )

  const handleTabChange = useCallback((value: string | null) => {
    setActiveTab(value)
    if (value !== 'room') return

    setDraft((prev) => ({
      ...prev,
      tables: ensureTablePositions(prev.tables, prev.room),
    }))
  }, [])

  const handleSave = useCallback(async () => {
    const sanitizedDraft = sanitizeSeatingState(draft, attendingGroups)
    const success = await onSave(sanitizedDraft)
    if (success) {
      notifications.show({
        title: 'Gespeichert',
        message: 'Der Sitzplan wurde erfolgreich gespeichert.',
        color: 'green',
      })
    } else {
      notifications.show({
        title: 'Fehler',
        message: 'Der Sitzplan konnte nicht gespeichert werden.',
        color: 'red',
        autoClose: 10000,
      })
    }
  }, [draft, attendingGroups, onSave])

  const handleTestGuestSubmit = useCallback((testGuestDraft: SeatingTestGuestDraft) => {
    setDraft((prev) => {
      const testGuest = testGuestDraftToSeatingTestGuest(testGuestDraft, prev.configurationId)
      const existingIndex = prev.testGuests.findIndex((tg) => tg.id === testGuest.id)

      if (existingIndex >= 0) {
        const nextTestGuests = [...prev.testGuests]
        nextTestGuests[existingIndex] = {
          ...testGuest,
          table_id: prev.assignments[testGuest.id] ?? null,
          created_at: prev.testGuests[existingIndex]!.created_at,
        }
        return { ...prev, testGuests: nextTestGuests }
      }

      return {
        ...prev,
        testGuests: [...prev.testGuests, testGuest],
        assignments: { ...prev.assignments, [testGuest.id]: null },
      }
    })
    setEditingTestGuest(null)
  }, [])

  const handleConfirmDeleteTestGuest = useCallback(() => {
    if (!deleteGroup) return

    setDraft((prev) => {
      const nextAssignments = { ...prev.assignments }
      delete nextAssignments[deleteGroup.id]
      return {
        ...prev,
        testGuests: prev.testGuests.filter((tg) => tg.id !== deleteGroup.id),
        assignments: nextAssignments,
      }
    })
    setDeleteGroup(null)
  }, [deleteGroup])

  const handleConfirmPromote = useCallback(async () => {
    if (!promoteGroup) return

    const sanitizedDraft = sanitizeSeatingState(draft, attendingGroups)
    const success = await onPromoteTestGuest(promoteGroup.id, sanitizedDraft)
    if (success) {
      await onGuestsChanged()
      notifications.show({
        title: 'Promoted',
        message: `${promoteGroup.displayName} ist jetzt ein echter Gast mit Zusage.`,
        color: 'green',
      })
      setPromoteGroup(null)
    } else {
      notifications.show({
        title: 'Fehler',
        message: 'Testnutzer konnte nicht promotet werden.',
        color: 'red',
        autoClose: 10000,
      })
    }
  }, [attendingGroups, draft, onGuestsChanged, onPromoteTestGuest, promoteGroup])

  const totalPeople = allGroups.reduce((sum, g) => sum + g.personCount, 0)
  const testGuestCount = draft.testGuests.length

  return (
    <Stack gap="sm">
      <SeatingConfigurationBar
        configurations={configurations}
        activeConfigurationId={activeConfigurationId}
        activeConfigurationName={activeConfigurationName}
        configBusy={configBusy}
        hasUnsavedChanges={hasChanges}
        onSelectConfiguration={onSelectConfiguration}
        onCreateConfiguration={onCreateConfiguration}
        onUpdateConfigurationName={onUpdateConfigurationName}
        onDeleteConfiguration={onDeleteConfiguration}
      />

      <Group justify="space-between" wrap="wrap" gap="xs">
        <Group gap="xs">
          <Button
            size="compact-sm"
            leftSection={<IconPlus size={14} />}
            variant="light"
            onClick={handleAddTable}
          >
            Tisch hinzufügen
          </Button>
          <Button
            size="compact-sm"
            leftSection={<IconFlask size={14} />}
            variant="light"
            color="grape"
            onClick={() => {
              setEditingTestGuest(null)
              setTestGuestModalOpen(true)
            }}
          >
            Testnutzer
          </Button>
          <Button
            size="compact-sm"
            leftSection={<IconArrowsShuffle size={14} />}
            variant="light"
            onClick={handleAutoDistribute}
          >
            Auto verteilen
          </Button>
          <Text size="sm" c="dimmed" className={styles.statsText}>
            {attendingGroups.length} Zusagen + Brautpaar
            {testGuestCount > 0 ? ` + ${testGuestCount} Test` : ''} ·{' '}
            <strong>{totalPeople} Personen</strong>
          </Text>
        </Group>
        <Group gap="xs">
          <Button
            size="compact-sm"
            leftSection={<IconRefresh size={14} />}
            variant="default"
            onClick={handleReset}
            disabled={!hasChanges}
          >
            Zurücksetzen
          </Button>
          <Button
            size="compact-sm"
            leftSection={<IconDeviceFloppy size={14} />}
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges}
          >
            Speichern
          </Button>
        </Group>
      </Group>

      <Tabs value={activeTab} onChange={handleTabChange} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="assignment" leftSection={<IconLayoutGrid size={14} />}>
            Zuweisung
          </Tabs.Tab>
          <Tabs.Tab value="room" leftSection={<IconMap2 size={14} />}>
            Raumplan
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="assignment" pt="sm">
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <Stack gap="sm">
              <UnassignedZone
                groups={unassignedGroups}
                activeDragId={activeDragId}
                onEditTestGuest={testGuestHandlers.onEditTestGuest}
                onDeleteTestGuest={testGuestHandlers.onDeleteTestGuest}
                onPromoteTestGuest={testGuestHandlers.onPromoteTestGuest}
              />

              {draft.tables.length === 0 ? (
                <Paper p="md" withBorder className={styles.noTables}>
                  <Text size="sm" ta="center" c="dimmed">
                    Noch keine Tische vorhanden.
                  </Text>
                </Paper>
              ) : (
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4, xl: 5 }} spacing="xs">
                  {draft.tables.map((table) => (
                    <SeatingTableCard
                      key={table.id}
                      table={table}
                      groups={getGroupsForTable(table.id, allGroups, draft.assignments)}
                      allGroups={allGroups}
                      assignments={draft.assignments}
                      onNameChange={handleTableNameChange}
                      onRemove={handleRemoveTable}
                      activeDragId={activeDragId}
                      {...testGuestHandlers}
                    />
                  ))}
                </SimpleGrid>
              )}
            </Stack>

            <DragOverlay>
              {activeGroup ? <GuestGroupCard group={activeGroup} isDragging /> : null}
            </DragOverlay>
          </DndContext>
        </Tabs.Panel>

        <Tabs.Panel value="room" pt="sm">
          <RoomPlanEditor
            tables={placedTables}
            room={draft.room}
            allGroups={allGroups}
            assignments={draft.assignments}
            onMoveTable={handleMoveTable}
            onRoomChange={handleRoomChange}
          />
        </Tabs.Panel>
      </Tabs>

      <TestGuestModal
        opened={testGuestModalOpen}
        onClose={() => {
          setTestGuestModalOpen(false)
          setEditingTestGuest(null)
        }}
        initialDraft={editingTestGuest}
        onSubmit={handleTestGuestSubmit}
      />

      <PromoteTestGuestModal
        opened={Boolean(promoteGroup)}
        onClose={() => setPromoteGroup(null)}
        group={promoteGroup}
        loading={promoting}
        onConfirm={handleConfirmPromote}
      />

      <Modal
        opened={Boolean(deleteGroup)}
        onClose={() => setDeleteGroup(null)}
        title="Testnutzer entfernen"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">
            Soll der Testnutzer <strong>{deleteGroup?.displayName}</strong> aus diesem Sitzplan
            entfernt werden?
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteGroup(null)}>
              Abbrechen
            </Button>
            <Button color="red" onClick={handleConfirmDeleteTestGuest}>
              Entfernen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}
